import type { SRT_Column, SRT_RowData, SRT_TableInstance } from '../../types'

import React, { createContext, use, useEffect, useMemo, useState } from 'react'
import { all, any } from 'lodash/fp'
import { Search, SettingsIcon, UndoIcon, XIcon } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/shared/components/ui/sheet'
import { useDebounce } from '@/shared/hooks/use-debounce'

import { SRT_TableColumnsReorderingList } from './SRT_TableColumnsReorderingList'

export function SRT_TableViewOptionsSheet<T extends SRT_RowData>({
	table,
	restore,
}: {
	table: SRT_TableInstance<T>
	restore: () => void
}) {
	const {
		options: { localization },
	} = table

	return (
		<Sheet modal={false}>
			<SheetTrigger asChild>
				<Button size='icon'>
					<SettingsIcon />
				</Button>
			</SheetTrigger>
			<SheetContent disbleOverlay className='gap-0'>
				<SheetHeader className='pb-2'>
					<SheetTitle>{localization.showHideColumns}</SheetTitle>
					<div>
						<Button onClick={restore} size='sm'>
							<UndoIcon />
							{localization.resetColumns}
						</Button>
					</div>
				</SheetHeader>
				<SRT_TableColumnsConfig table={table}>
					<div className='flex flex-col overflow-hidden pt-1'>
						<div className='px-4 pb-2'>
							<SRT_TableColumnsConfigSearch table={table} />
						</div>
						<SRT_TableColumnsConfigReorderingListWithSearch table={table} />
					</div>
				</SRT_TableColumnsConfig>
			</SheetContent>
		</Sheet>
	)
}

export const SRT_TableColumnsConfigReorderingListWithSearch = <
	T extends SRT_RowData,
>({
	table,
}: {
	table: SRT_TableInstance<T>
}) => {
	const { searchTextDebounced } = useSRT_TableColumnsConfig()
	return (
		<div
			className='scroll-fade overflow-x-hidden overflow-y-auto px-2 pb-10'
			id='scrolllist'
		>
			<AllColumnsCheckbox table={table} />
			<SRT_TableColumnsReorderingList
				search={searchTextDebounced}
				table={table}
			/>
		</div>
	)
}

const AllColumnsCheckbox = <T extends SRT_RowData>({
	table,
}: {
	table: SRT_TableInstance<T>
}) => {
	const {
		options: { localization },
	} = table
	const columns = table.getAllLeafColumns()
	const isAllSelected = useMemo(
		() => all((column) => column.getIsVisible(), columns),
		[columns]
	)
	const isAnySelected = useMemo(
		() => any((column) => column.getIsVisible(), columns),
		[columns]
	)

	return (
		<label className='mb-1 flex w-full items-center gap-2 p-2'>
			<Checkbox
				checked={isAllSelected}
				indeterminate={isAnySelected && !isAllSelected}
				onChange={() => {
					if (isAllSelected) {
						columns.forEach((column) => column.toggleVisibility(false))
					} else {
						columns.forEach((column) => column.toggleVisibility(true))
					}
				}}
			/>
			<span className='text-sm font-semibold'>{localization.selectAll}</span>
		</label>
	)
}

const scrollToNode = (searchText: string) => {
	const scrl = document.getElementById('scrolllist')
	const nodes = Array.from(
		document.querySelectorAll('#scrolllist div[data-col-name]')
	)
	const node = nodes?.find(
		(item) =>
			item
				.getAttribute('data-col-name')
				?.toLowerCase()
				.includes(searchText?.toLowerCase()) ||
			item
				.getAttribute('data-parent-col-name')
				?.toLowerCase()
				.includes(searchText?.toLowerCase())
	)
	if (node) {
		scrl?.scrollTo({
			top: (node as HTMLElement).offsetTop - (scrl as HTMLElement)?.offsetTop,
			left: 0,
			behavior: 'smooth',
		})
	}
}

export type SRT_TableColumnsConfigContextValue = {
	searchText: string
	count: number
	searchTextDebounced: string
	setSearchText: (searchText: string) => void
}

export const SRT_TableColumnsConfigContext =
	createContext<SRT_TableColumnsConfigContextValue | null>(null)
export const SRT_TableColumnsConfig = <T extends SRT_RowData>({
	table,
	children,
}: {
	table: SRT_TableInstance<T>
	children: React.ReactNode
}) => {
	const [searchText, setSearchText] = useState('')
	const searchTextDebounced = useDebounce(searchText, 500)
	useEffect(() => {
		if (searchTextDebounced) {
			scrollToNode(searchTextDebounced)
		}
	}, [searchTextDebounced])

	const count = useMemo(
		() =>
			table.getAllLeafColumns().filter((column: SRT_Column<T>) => {
				if (
					searchTextDebounced &&
					column.columnDef.header &&
					typeof column.columnDef.header === 'string'
				) {
					return column.columnDef.header
						.toLocaleLowerCase()
						.includes(searchTextDebounced.toLocaleLowerCase())
				}

				return false
			}).length,
		[searchTextDebounced]
	)
	return (
		<SRT_TableColumnsConfigContext.Provider
			value={{ searchText, setSearchText, count, searchTextDebounced }}
		>
			{children}
		</SRT_TableColumnsConfigContext.Provider>
	)
}
export const SRT_TableColumnsConfigSearch = <T extends SRT_RowData>({
	table,
}: {
	table: SRT_TableInstance<T>
}) => {
	const {
		options: { localization },
	} = table
	const { searchText, setSearchText, count } = useSRT_TableColumnsConfig()
	return (
		<InputGroup>
			<InputGroupInput
				value={searchText}
				onChange={(e) => {
					setSearchText(e.target.value)
				}}
				placeholder={localization.search}
			/>
			<InputGroupAddon>
				<Search />
			</InputGroupAddon>

			{!!searchText && (
				<InputGroupAddon align='inline-end'>
					{' '}
					{searchText && count ? count : null}
				</InputGroupAddon>
			)}
			{!!searchText && (
				<InputGroupAddon align='inline-end'>
					<InputGroupButton size='icon-xs' onClick={() => setSearchText('')}>
						<XIcon />
					</InputGroupButton>
				</InputGroupAddon>
			)}
		</InputGroup>
	)
}

export const useSRT_TableColumnsConfig = () => {
	const context = use(SRT_TableColumnsConfigContext)
	if (!context) {
		throw new Error(
			'useTableColumnsConfig must be used within a TableColumnsConfig'
		)
	}
	return context
}
